import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Quote, Star, ArrowLeft, Share2 } from "lucide-react";

export default function TruthWallPage() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [voted, setVoted] = useState(false);
  const [page, setPage] = useState({ voting_enabled: true, show_votes: true });

  useEffect(() => {
    const votedSet = new Set(JSON.parse(localStorage.getItem("tw_voted") || "[]"));
    setVoted(votedSet.has(id));
    http.get("/truth-wall/page").then(({ data }) => setPage(data)).catch(() => {});
    http.get(`/truth-wall/truths/${id}`)
      .then(({ data }) => setT(data))
      .catch(() => setNotFound(true));
  }, [id]);

  const vote = async () => {
    if (voted) return;
    try {
      const { data } = await http.post(`/truth-wall/truths/${id}/vote`);
      const next = new Set(JSON.parse(localStorage.getItem("tw_voted") || "[]"));
      next.add(id);
      localStorage.setItem("tw_voted", JSON.stringify([...next]));
      setVoted(true);
      setT({ ...t, votes: data.votes ?? (t.votes || 0) + 1 });
      toast.success("Thank you for voting.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vote failed.");
    }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: "A truth worth sharing", text: `"${t.text}"`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied.");
      }
    } catch { /* user canceled */ }
  };

  if (notFound) {
    return (
      <Layout>
        <section className="bg-[#050505] min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-6">
            <div className="font-display text-2xl text-[#F4F1EA] mb-3">Truth not found.</div>
            <div className="font-body text-[#8a857d] mb-6">It may have been removed or is awaiting approval.</div>
            <Link to="/truth-wall" className="btn-gold">Back to The Truth Wall</Link>
          </div>
        </section>
      </Layout>
    );
  }

  if (!t) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center text-[#8a857d]">Loading…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="relative bg-[#050505] pt-20 pb-12 sm:pt-28 border-b border-white/5" data-testid="tw-page-hero">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/truth-wall" data-testid="tw-page-back"
                className="font-ui text-[10px] tracking-[0.22em] uppercase text-[#8a857d] hover:text-[#C9A055] inline-flex items-center gap-1.5 mb-8">
            <ArrowLeft size={11} /> Back to The Truth Wall
          </Link>

          {t.featured && (
            <div className="inline-flex items-center gap-1.5 font-ui text-[10px] tracking-[0.22em] uppercase text-[#C9A055] mb-5">
              <Star size={11} fill="#C9A055" /> Featured Truth
            </div>
          )}

          <Quote size={32} className="text-[#C9A055] opacity-70 mb-5" />
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#F4F1EA] leading-[1.2] mb-6"
              data-testid="tw-page-text">
            &ldquo;{t.text}&rdquo;
          </h1>

          <div className="font-ui text-[11px] tracking-[0.22em] uppercase text-[#8a857d]">
            {t.name || "Anonymous"}
            {t.city ? ` · ${t.city}${t.state ? `, ${t.state}` : ""}` : ""}
          </div>
        </div>
      </section>

      {t.story && (
        <section className="bg-[#0a0a0a] py-14 sm:py-20 border-b border-white/5" data-testid="tw-page-story">
          <div className="max-w-2xl mx-auto px-6">
            <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-5">The Story</div>
            <div className="font-body text-[16px] leading-[1.9] text-[#E8E4DC] whitespace-pre-wrap italic">
              {t.story}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#050505] py-14 sm:py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="font-ui text-[10px] tracking-[0.32em] uppercase text-[#C9A055] mb-4">If this resonates</div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {page.voting_enabled && (
              <button onClick={vote} data-testid="tw-page-vote"
                      className={`flex items-center gap-2 font-ui text-[11px] tracking-[0.22em] uppercase px-5 py-3 border transition ${voted ? "border-[#C9A055] text-[#C9A055]" : "border-white/15 text-[#F4F1EA] hover:text-[#C9A055] hover:border-[#C9A055]"}`}>
                <Heart size={13} fill={voted ? "#C9A055" : "none"} />
                {voted ? "You voted" : "Vote for this truth"}
                {page.show_votes && (
                  <span className="ml-1 text-[#8a857d]">· {t.votes ?? 0}</span>
                )}
              </button>
            )}
            <button onClick={share} data-testid="tw-page-share"
                    className="flex items-center gap-2 font-ui text-[11px] tracking-[0.22em] uppercase px-5 py-3 border border-white/15 text-[#F4F1EA] hover:text-[#C9A055] hover:border-[#C9A055] transition">
              <Share2 size={13} /> Share
            </button>
            <Link to="/truth-wall" className="btn-gold">More Truths</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
